import { useEffect, useMemo, useState } from 'react'

import { TreeItemIndex } from 'react-complex-tree'

import { QuerySpacesArgs } from '@bettermode/common/gql/generated'
import { useI18n } from '@tribeplatform/react-components/i18n'

import { DEFAULT_TREE_ITEM_LIMIT } from '../constants.js'
import { createLoadingTreeItem } from '../tree-items/createLoadingTreeItem.js'
import { createLoadMoreTreeItem } from '../tree-items/createLoadMoreTreeItem.js'
import { createShowOrLessItem } from '../tree-items/createShowOrLessTreeItem.js'
import { createSpaceTreeItem } from '../tree-items/createSpaceTreeItem.js'
import { TreeItems } from '../types.js'

import { useCollectionSpaces } from './useCollectionSpaces.js'

export function useCollectionSpacesTreeItems({
  id,
  variables,
  enabled,
  hideLoading,
  ensureVisible,
}: {
  id: TreeItemIndex
  variables: Omit<QuerySpacesArgs, 'limit'>
  enabled: boolean
  hideLoading?: boolean
  ensureVisible?: TreeItemIndex
}) {
  const intl = useI18n()
  const [showOrLess, setShowOrLess] = useState<'show' | 'less'>('show')
  const {
    items: spaces,
    isLoading,
    loadRest,
    loadedAll,
  } = useCollectionSpaces({
    variables,
    enabled,
  })

  // When a specific space must be visible but may be beyond the initial page,
  // load all spaces for this collection so the item appears in the tree.
  useEffect(() => {
    if (ensureVisible && !loadedAll && !isLoading) {
      loadRest()
    }
  }, [ensureVisible, loadedAll, isLoading, loadRest])

  const truncatedSpaces = useMemo(() => {
    if (spaces.length <= DEFAULT_TREE_ITEM_LIMIT || showOrLess === 'show') {
      return spaces
    }

    return spaces.slice(0, DEFAULT_TREE_ITEM_LIMIT)
  }, [spaces, showOrLess])

  const spacesTreeItems = useMemo(() => {
    return truncatedSpaces.reduce(
      (acc, s) => ({
        ...acc,
        ...createSpaceTreeItem(s),
      }),
      {} as TreeItems,
    )
  }, [truncatedSpaces])

  const loadMoreTreeItem = useMemo(() => {
    if (loadedAll) {
      return {}
    }

    return createLoadMoreTreeItem(intl, {
      id,
      onClick: loadRest,
    })
  }, [intl, loadedAll, loadRest, id])

  const showMoreOrLessItem = useMemo(() => {
    if (spaces.length <= DEFAULT_TREE_ITEM_LIMIT) {
      return {}
    }

    return createShowOrLessItem(intl, {
      id,
      showOrLess,
      setShowOrLess,
    })
  }, [intl, showOrLess, id, spaces])

  return useMemo(
    () => ({
      ...spacesTreeItems,
      ...(isLoading && !hideLoading ? createLoadingTreeItem(intl) : {}),
      ...loadMoreTreeItem,
      ...showMoreOrLessItem,
    }),
    [
      intl,
      spacesTreeItems,
      loadMoreTreeItem,
      isLoading,
      showMoreOrLessItem,
      hideLoading,
    ],
  )
}
